import "./_personnalisationOffreAuMagasin.scss";

import Decimal from "decimal.js";
import debounce from "lodash.debounce";
import {ReactElement, useContext, useEffect} from "react";
import {Controller, useWatch} from "react-hook-form";

import {InputFile} from "@/components/InputFile/InputFile";
import InputLabel from "@/components/InputLabel/InputLabel";
import MultiSelect from "@/components/MultiSelect/MultiSelect";
import SelectCustom from "@/components/SelectCustom/SelectCustom";
import {HookFormContext} from "@/context/HookFormContext";
import {
  CalculeQuantitéForPalier,
  getAllPrix,
  getOptionFormat,
  getPrixPalier,
} from "@/function/operationCommercialeToole";
import {OptionNameValue} from "@/ts/interface/formulaire";
import {Magasin} from "@/ts/interface/magasin";
import {
  FormSelectedMagasin,
  OffreOperationCommerciale,
} from "@/ts/interface/operationCommerciale";

function PersonnalisationOffreAuMagasin({
  offre,
  magasin,
}: {
  offre: OffreOperationCommerciale;
  magasin: Magasin;
}): ReactElement {
  const context = useContext(HookFormContext);

  if (!context) {
    throw new Error("HookFormContext is not provided.");
  }

  const {
    getValues,
    watch,
    control,
    setValue,
    register,
    formState: {errors},
    printError,
    atLeastOneFilled,
  } = context;

  const rerenderLabel = `offres.O${offre.offre_id}.magasins.M${magasin.magasin_id}.rerender`;
  const rerenderValues = useWatch({
    control,
    name: rerenderLabel,
  });
  const selectedMagasin: FormSelectedMagasin[] = getValues(
    `offres.O${offre.offre_id}.selected_magasins`,
  );
  useEffect(() => {
    // Ajoute les valeur par défaut pour chaque item de l'offre
    // En fonction des information déjà renseigner comme les quanités de livraison
    offre.items.forEach(item => {
      const itemName = `offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}`;
      setValue(`${itemName}.nom`, item.nom);
      setValue(`${itemName}.forfait`, item?.forfait);
      item.livraisons.forEach(livraison => {
        setValue(
          `${itemName}.livraisons.L${livraison.livraison_id}.prixPalier`,
          livraison.prix,
        );
      });

      const allMagasinsWithItem = getValues(
        `offres.O${offre.offre_id.toString()}.magasins`,
      );
      // permet de connaitre la quantité total commandé pour calculer les palier
      const currentQuantite = CalculeQuantitéForPalier(
        item.item_id,
        getValues(itemName),
        allMagasinsWithItem,
      );
      const formatName = `${itemName}.format`;
      const prixUnitairePalier = getPrixPalier(
        getValues(`${formatName}.complementData`),
        currentQuantite <= 0 ? 1 : currentQuantite,
      );
      if (prixUnitairePalier !== "") {
        setValue(`${formatName}.prix_unitaire`, prixUnitairePalier);
      }
    });
  }, [rerenderValues]);
  const allMagasins = getValues(
    `offres.O${offre.offre_id.toString()}.magasins`,
  );
  // permet de refresh tout les magasins afin de calculer les nouveaux paliers
  const handelChange = debounce((): void => {
    for (const key in allMagasins) {
      console.log("lalalal");
      const rerender = getValues(rerenderLabel) || 0;
      setValue(
        `offres.O${offre.offre_id}.magasins.${key}.rerender`,
        rerender + 1,
      );
    }
  }, 400);
  // Récupère tout les nom des item
  const allItemName: string[] = [];
  offre.items.map(itemValue => {
    allItemName.push(
      `offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${itemValue.item_id}.format.value`,
    );
  });
  const PersonnalisationComponent = offre?.items.map(item => {
    const itemName = `offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}`;
    const allMagasinsWithItem = getValues(
      `offres.O${offre.offre_id.toString()}.magasins`,
    );
    // permet de connaitre la quantité total commandé pour calculer les palier
    const currentQuantite = CalculeQuantitéForPalier(
      item.item_id,
      getValues(itemName),
      allMagasinsWithItem,
    );
    const {formats, options} = getOptionFormat(item, currentQuantite || 1);
    const formatName = `offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}.format`;
    const formatValue = getValues(`${formatName}.value`);
    // Récupère tout les nom de livraison pour vérifier si au moins une livraison est remplie
    const allLivraisonName: string[] = [];
    item.livraisons.map(livraison => {
      allLivraisonName.push(
        `${itemName}.livraisons.L${livraison.livraison_id}.quantite`,
      );
    });
    // La livraison est requise en cas de format
    const livraisonIsRequire = formatValue
      ? atLeastOneFilled(
          allLivraisonName,
          getValues,
          "Mettez une quantité sur au moins une livraison",
        )
      : false;
    const allLivraison = item?.livraisons.map(livraison => {
      const quantiteName = `offres.O${offre.offre_id}.magasins.M${magasin.magasin_id}.items.I${item.item_id}.livraisons.L${livraison.livraison_id}.quantite`;

      const {TotalPrixItemLivraison, prixUnitaireTotal, prixUnitaireLivraison} =
        getAllPrix(
          getValues(`${formatName}`),
          livraison.prix,
          getValues(`${itemName}.options`),
          currentQuantite,
          getValues(quantiteName),
        );
      const prestataireDistribution: OptionNameValue[] =
        magasin.prestataire_distribution?.map(prestataire => {
          return {
            libelle: prestataire.nom,
            value: prestataire.prestataire_distribution_id,
          };
        }) || [];
      const havePrestataire =
        livraison.type === "BAL" && getValues(quantiteName);
      const haveRoutage =
        livraison.type === "Routage" && getValues(quantiteName);

      const placeholderValue = (): string => {
        if (
          livraison?.qte_min &&
          livraison?.qte_max &&
          livraison?.qte_max < 9999999
        ) {
          return `${livraison?.qte_min} - ${livraison?.qte_max}`;
        } else {
          return `Min(${livraison?.qte_min | 1})`;
        }
      };
      return (
        <div
          className="offrePersonnalisation-livraison--type"
          key={livraison.livraison_id}>
          <InputLabel
            label={livraison.type}
            id={livraison.type}
            min={livraison.qte_min}
            max={livraison.qte_max || 9999999}
            placeholder={placeholderValue()}
            onKeyDown={e => {
              if (e.key === "Enter") e.preventDefault();
            }}
            disabled={!formatValue}
            type="number"
            complementLibelle={
              prixUnitaireLivraison
                ? `(+${new Decimal(prixUnitaireLivraison)}€/u)`
                : ""
            }
            defaultValue={getValues(quantiteName) || ""}
            {...register(quantiteName, {
              onChange: () => handelChange(),
              valueAsNumber: true,
              required: livraisonIsRequire,
            })}
          />
          {havePrestataire ? (
            <div id="prestataireDistribution">
              <div className="offreFormat-livraison--prestataire">
                {printError(
                  `offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}.prestataire_distribution.message`,
                  errors,
                )}
                <SelectCustom
                  defaultValue=""
                  label={"Prestataire de distribution"}
                  placeholder="-- Choix du prestataire --"
                  options={prestataireDistribution}
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  handleSelect={() => {
                    const rerender = getValues(rerenderLabel) || 0;

                    setValue(rerenderLabel, rerender + 1);

                    // Déclenche le changement du champ via field.onChange
                  }}
                  setValue={setValue}
                  watch={watch}
                  {...register(
                    `offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}.prestataire_distribution`,
                    {
                      required: havePrestataire
                        ? "Choisissez un prestataire de distribution"
                        : false,
                    },
                  )}
                />
              </div>
            </div>
          ) : null}

          {livraison.type === "Routage" && getValues(quantiteName) ? (
            <div id="prestataireDistribution">
              <div className="offreFormat-livraison--prestataire">
                <InputFile
                  control={control}
                  name={`offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}.livraisons.L${livraison.livraison_id}.fichiers[0].fichier`}
                  label={magasin.raison_sociale}
                  isRequire={
                    haveRoutage
                      ? "Ajoutez un seul fichier de routage dans 1 magasin ou un fichier pour chaque magasin."
                      : false
                  }
                  handleDeleteFile={() => {
                    console.log("delete");
                    setValue(
                      `offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}.livraisons.L${livraison.livraison_id}.fichiers[0].fichier_id`,
                      undefined,
                    );
                  }}
                />
              </div>
            </div>
          ) : null}
          {parseFloat(TotalPrixItemLivraison) > 0 ? (
            <p className="offrePersonnalisation-prix">
              {`${new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(Number(TotalPrixItemLivraison))}`}
              <br />
              <span>{`Soit :  ${prixUnitaireTotal}€/u`}</span>
            </p>
          ) : null}
        </div>
      );
    });

    // Génère une erreur de formulaire si aucun item ne possède de format
    const itemFormatIsRequire = atLeastOneFilled(
      allItemName,
      getValues,
      allItemName.length > 1
        ? "Choisissez au moins 1 item"
        : "Choisissez le format",
    );

    const prixUnitaireFormat = getValues(`${formatName}.prix_unitaire`);
    // affiche le message d'erreur du routage
    const MessageErreurFichierRoutage =
      selectedMagasin?.reduce<React.ReactNode | null>((acc, mag) => {
        if (acc) return acc;

        const errorMessage = item?.livraisons?.reduce<React.ReactNode | null>(
          (acc, livraison) => {
            if (acc) return acc;
            if (livraison.type === "Routage") {
              return printError(
                `offres.O${offre.offre_id.toString()}.magasins.M${mag.value}.items.I${item.item_id}.livraisons.L${livraison.livraison_id}.fichiers.0.fichier.message`,
                errors,
              );
            }
          },
          null,
        );
        return errorMessage;
      }, null);

    return (
      <div className="offrePersonnalisation-parametrage" key={item.item_id}>
        {MessageErreurFichierRoutage}
        <div className="offrePersonnalisation-parametrage--formatOption">
          <Controller
            name={formatName}
            control={control}
            rules={{required: itemFormatIsRequire}}
            render={({field}) => {
              useEffect(() => {
                const formatIndex = formats.findIndex(form => {
                  return form.value === field.value?.value;
                });
                if (formatIndex >= 0) {
                  field.value.complementData =
                    formats[formatIndex].complementData;
                }
              }, []);
              return (
                <>
                  <SelectCustom
                    defaultValue=""
                    label={item.nom}
                    placeholder="-- Selectionner un format --"
                    options={formats}
                    handleSelect={() => {
                      handelChange();
                    }}
                    onTrash={() => {
                      field.onChange(undefined);
                      // reset les options
                      setValue(`${itemName}.options`, []);
                      const livraisons = getValues(`${itemName}.livraisons`);
                      //reset les livraisons
                      Object.entries(livraisons)?.forEach(([key]) => {
                        setValue(`${itemName}.livraisons.${key}.quantite`, "");
                      });

                      handelChange();
                    }}
                    setValue={setValue}
                    watch={watch}
                    complementLabel={
                      prixUnitaireFormat
                        ? `(+${new Decimal(prixUnitaireFormat).toFixed(2)}€/u)`
                        : ""
                    }
                    {...field}
                  />
                </>
              );
            }}
          />
          {printError(`${formatName}.message`, errors)}

          {item?.forfait && parseFloat(item?.forfait) > 0 ? (
            <p className="offreOperation-prixForfait">
              {`Prix forfait ${item.nom} : ${new Decimal(item.forfait).toDecimalPlaces(2, Decimal.ROUND_HALF_UP).toFixed(2)} €`}{" "}
              <br />
              <span>
                (S'applique une seule fois, quelle que soit la quantité
                commandée)
              </span>
            </p>
          ) : null}
        </div>
        <div className="offrePersonnalisation-livraison">
          {/* verifie si une erreur existe sur la première livraison et la retourne.
              Les erreurs sont commune entre toutes les livraisons. */}
          {printError(`${allLivraisonName[0]}.message`, errors)}
          {allLivraison}
        </div>
        <div className="offrePersonnalisation-option">
          {(item.options?.length || 0) > 0 ? (
            <MultiSelect
              label="OPTION"
              options={options}
              disabled={!formatValue}
              name={`offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}.items.I${item.item_id}.options`}
              uniqueKey={`offres.O${offre.offre_id.toString()}.magasins.M${magasin.magasin_id}`}
              onClick={() => {
                handelChange();
              }}
              watch={watch}
              nbLigne={4}
              hasBorder={false}
              hasButtonAllSelect={false}
              control={control}
              setValue={setValue}
            />
          ) : null}
        </div>
      </div>
    );
  });

  return (
    <div className="offrePersonnalisation">
      <h3>{magasin.raison_sociale}</h3>
      {PersonnalisationComponent}
    </div>
  );
}

export {PersonnalisationOffreAuMagasin};
